import React, { useState, useContext, useEffect } from 'react'
import { z } from 'zod'
import styled from 'styled-components'
import { Button, Checkbox, Modal, Input, Form, notification, Typography } from 'antd'
import { FormattedMessage } from 'react-intl'

import { ParticipantCourseContext } from '../Context/ParticipantCourseContext'
import { AlertTriangle } from 'react-feather'
import { FlexRow } from '../../FlexboxReusables/FlexItems'


// TODO: i18n this whole component


const GDPR_CONSENT_INTRO = `Hiermit willigen Sie ausdrücklich ein, dass ihr Name und Geburtsdatum

vom Betreiber der Webseite ausschließlich zum Zweck der Erinnerung an das Ablaufdatum der von mir absolvierten Unterweisung gespeichert und verarbeitet werden.

Die Speicherung erfolgt für die Dauer von genau einem Jahr ab dem Datum der erfolgten erfolgreichen Unterweisungsprüfung.

Nach Ablauf dieses Zeitraums werden meine Daten unverzüglich gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.

Eine anderweitige Nutzung, Verarbeitung oder Weitergabe meiner Daten ist ausdrücklich ausgeschlossen.`

const GDPR_CONSENT_LIST_ITEMS = [
  'die Erteilung dieser Einwilligung freiwillig erfolgt,',
  'sie Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen können,',
  'Ihnen gemäß der Datenschutz-Grundverordnung (DSGVO) die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung sowie Beschwerde bei einer Datenschutzaufsichtsbehörde zustehen.'
]

const GDPR_CONSENT_OUTRO = 'Verantwortlicher im Sinne der DSGVO ist die Matrix Helvetica GmbH, Fahrgasse 3, CH- 9443 Widnau.'

const StartExamModal = () => {
  const {
    isStartExamModalVisible,
    setIsStartExamModalVisible,
    startExam,
    maxExamTriesExceeded,
    maxExamParticipantsExceeded,
    originalLinkForParticipants
  } = useContext(ParticipantCourseContext)

  const [ showInputFields, setShowInputFields ] = useState(false)
  const [ firstName, setFirstName ] = useState('')
  const [ lastName, setLastName ] = useState('')
  const [ birthDate, setBirthDate ] = useState('')
  const [ email, setEmail ] = useState('')
  const [ gdprConsentChecked, setGdprConsentChecked ] = useState(false)
  const [ gdprModalOpen, setGdprModalOpen ] = useState(false)

  useEffect(() => {
    const userData = localStorage.getItem('examUserData')

    if (userData) {
      const { firstName, lastName, birthDate, email } = JSON.parse(userData)
      setFirstName(firstName)
      setLastName(lastName)
      setBirthDate(birthDate)
      setEmail(email)
    }
  }, [])

  /**
   * @description avoids leading/trailing whitespaces in Database
   */
  const trimInput = (input: string) => input.trim()

  const handleConfirmClick = () => {
    if (!gdprConsentChecked) {
      notification.error({
        message: 'Einverständniserklärung erforderlich',
        description: 'Bitte bestätigen Sie die Einverständniserklärung zur Speicherung personenbezogener Daten.',
        placement: 'bottomRight',
      })
      return
    }

    const trimmedFirstName = trimInput(firstName)
    const trimmedLastName = trimInput(lastName)
    const trimmedEmail = trimInput(email)
  
    const inputValidation = userInputSchema.safeParse({
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      birthDate,
      email: trimmedEmail
    })
  
    if (inputValidation.success) {
      localStorage.setItem('examUserData', JSON.stringify({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        birthDate,
        email: trimmedEmail
      }))
  
      startExam({
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        birthDate,
        email: trimmedEmail,
        originalLinkForParticipants
      })
    } else {
      notification.error({
        message: 'Validierungsfehler',
        description: (
          <ul>
            {inputValidation.error.errors.map((err, index) => (
              <li key={index}>{err.message}</li>
            ))}
          </ul>
        ),
        placement: 'bottomRight',
      })
    }
  }

  const clearUserData = () => {
    setFirstName('')
    setLastName('')
    setBirthDate('')
    localStorage.removeItem('examUserData')
  }

  const userHasDataInLocalStorage = () => !!localStorage.getItem('examUserData')

  const renderModalsConditionally = () => {
    if (maxExamTriesExceeded) {
      return renderErrorModal('Versuchen')
    }

    if (maxExamParticipantsExceeded) {
      return renderErrorModal('Prüfungsvorgängen')
    }

    return renderStartExamModal()
  }

  const renderStartExamModal = () => {
    return (
      <>
        <Modal
          centered
          title={<FormattedMessage id='exam.startExamModal.title' />}
          open={isStartExamModalVisible}
          onCancel={() => setIsStartExamModalVisible(false)}
          footer={[
            <Button key="back" onClick={() => setIsStartExamModalVisible(false)}>
              <FormattedMessage id='generic.cancel' />
            </Button>,
            !showInputFields ? (
              <Button key="start" type="primary" onClick={() => setShowInputFields(true)}>
                <FormattedMessage id='generic.next' />
              </Button>
            ) : (
              <>
                {userHasDataInLocalStorage() && (
                  <Button key="clear" onClick={clearUserData}>
                    Daten zurücksetzen
                  </Button>
                )}
                <Button
                  key="confirm"
                  type="primary"
                  onClick={handleConfirmClick}
                  disabled={!gdprConsentChecked}
                >
                  <FormattedMessage id='generic.start' />
                </Button>

              </>
            ),
          ]}
        >
          {!showInputFields ? (
            <>
              <p>
                <FormattedMessage id='exam.startExamModal.message1' />
              </p>
              <p>
                <FormattedMessage id='exam.startExamModal.message2' />
              </p>
            </>
          ) : (
            <>
              <FlexRow justifyContent='flex-start' margin='1.5em 0'>
                <Typography.Text>
                  Bitte geben Sie Ihre Daten ein, um die Prüfung zu starten.
                  <p />
                  Sollten Sie die Prüfung wiederholen müssen, müssen Sie bei jedem weiteren Versuch die <strong>exakt</strong> gleichen Daten verwenden.
                </Typography.Text>
              </FlexRow>
              <Form>
                <Form.Item label={<FormattedMessage id='generic.firstName' />} required>
                  <Input value={firstName} onChange={e => setFirstName(e.target.value)} />
                </Form.Item>
                <Form.Item label={<FormattedMessage id='generic.lastName' />} required>
                  <Input value={lastName} onChange={e => setLastName(e.target.value)} />
                </Form.Item>
                <Form.Item label={<FormattedMessage id='generic.birthDate' />} required>
                  <Input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} />
                </Form.Item>
                <EmailInfoText>
                  An diese Email-Adresse wird nach erfolgreicher Teilnahme der Link zum (erneuten) herunterladen des Zertifikats geschickt. Wir verwenden Ihre Email-Adresse niemals zu Werbe- oder Kontaktzwecken.
                </EmailInfoText>
                <Form.Item label={'Email-Adresse'} required>
                  <Input value={email} onChange={e => setEmail(e.target.value)} />
                </Form.Item>
                <Form.Item>
                  <Checkbox
                    checked={gdprConsentChecked}
                    onChange={e => setGdprConsentChecked(e.target.checked)}
                  >
                    <Typography.Text>
                      <GdprLink onClick={(e) => { e.preventDefault(); setGdprModalOpen(true) }} href='#'>
                        Einverständniserklärung
                      </GdprLink>
                      {' '}
                      zur Speicherung personenbezogener Daten gemäß DSGVO
                    </Typography.Text>
                  </Checkbox>
                </Form.Item>
              </Form>
            </>
          )}
        </Modal>

        <Modal
          title='Einverständniserklärung'
          open={gdprModalOpen}
          onCancel={() => setGdprModalOpen(false)}
          footer={[
            <Button key='close' type='primary' onClick={() => setGdprModalOpen(false)}>
              Schließen
            </Button>
          ]}
          centered
        >
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
            {GDPR_CONSENT_INTRO}
          </Typography.Paragraph>
          <Typography.Paragraph>Ihnen ist bekannt, dass:</Typography.Paragraph>
          <ul>
            {GDPR_CONSENT_LIST_ITEMS.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <Typography.Paragraph>{GDPR_CONSENT_OUTRO}</Typography.Paragraph>
        </Modal>
      </>
    )    
  }

  return (
    <>
      {renderModalsConditionally()}
    </>
  )
}

const renderErrorModal = (type: 'Prüfungsvorgängen' | 'Versuchen' ) => {
  return (
    <Modal
      centered
      title={(
        <FlexRow>
          <AlertIcon />
          {' '}
          {`Maximale Anzahl an ${type} erreicht.`}
        </FlexRow>
      )}
      open={true}
      footer={null}
      closable={false}
    >
      <p>
        {`Es tut uns Leid, die maximale Anzahl an ${type} wurde bereits erreicht.`}
      </p>
      <p>
        Bitte wenden Sie sich an Ihren Kursleiter.
      </p>
      <p>
        Sie können das Browserfenster bzw. den Tab nun schließen.
      </p>
    </Modal>
  )
}

export default StartExamModal

const AlertIcon = styled(AlertTriangle)`
  color: orange;
  margin-right: 0.5em;
`

const EmailInfoText = styled(Typography.Text)`
  margin-bottom: 1em;
  font-size: 0.8em;
  color: #666;
  display: block;
`

const GdprLink = styled.a`
  text-decoration: underline;
`

export const userInputSchema = z.object({
  firstName: z.string().min(2, "Der Vorname muss mindestens 2 Zeichen lang sein."),
  lastName: z.string().min(2, "Der Nachname muss mindestens 2 Zeichen lang sein."),
  birthDate: z.string()
    .refine(val => !isNaN(Date.parse(val)), {
      message: "Ungültiges Geburtsdatum. Bitte geben Sie das Datum im Format JJJJ.MM.TT an."
    })
    .transform(val => new Date(val)),
  email: z.string().email("Ungültige Email-Adresse")
})
